/*global chrome*/

import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import jwtDecode from 'jwt-decode'

import { TextField, PasswordField } from './Fields'
import { Logo } from '../LandingPage/Logo'
import { SlimLayout } from './SlimLayout'

import { login } from '../../services/authService'
import { loginWithPasskey } from '../../services/passkeyService'
import { baseURL } from '../../config'
import './authPages.css'
import TwoFactorVerification from './TwoFactorVerification'
import EmailCodeSignIn from './EmailCodeSignIn'
import posthogService from '../../services/posthogService'

import googleIcon from '../../media/icons/google.svg'

const sendTokenToChromeExtension = (extensionId, token) => {
	if (
		typeof chrome !== 'undefined' &&
		chrome.runtime &&
		chrome.runtime.sendMessage
	) {
		try {
			chrome.runtime.sendMessage(extensionId, token, response => {
				if (chrome.runtime.lastError) {
					console.log(
						'Chrome extension is not installed or not active:',
						chrome.runtime.lastError.message
					)
				} else {
					console.log('response', response)
				}
			})
		} catch (error) {
			console.error('Error sending message to Chrome extension:', error)
		}
	} else {
		console.log(
			'Chrome extension messaging API not available - non-Chrome browser or extension not installed.'
		)
	}
}

const productionExtensionId = 'ihcgicdogclbieclnldfjmlglkolankb'
const developmentExtensionId = 'omgobnjbempcolkjnfphklponcmlooko'
const extensionId = productionExtensionId

export default function Login({
	sourceExtension,
	setSourceExtension,
	windowOpender,
	userToken,
}) {
	let location = useLocation()
	const navigate = useNavigate()

	const authUrl = !baseURL ? '' : baseURL
	// Check if we're on localhost and adjust the URL accordingly
	let googleAuthUrl = `${
		window.location.hostname === 'localhost' ? 'http://localhost:5001' : authUrl
	}/auth/google${sourceExtension ? `?source=extension&token=${userToken}` : ''}`

	const [loading, setLoading] = useState(false)
	const [twoFactorData, setTwoFactorData] = useState(null)
	const [emailCodeMode, setEmailCodeMode] = useState(false)

	// True when this page will hand the extension its token and close, discarding
	// anything meant for the website.
	const extensionOnly = Boolean(sourceExtension && windowOpender)

	const [state, setState] = useState({
		email: '',
		password: '',
		errorMsg: null,
		loading: false,
		typeChanged: false,
		displayGoogleBtn: true,
		errorText: '',
	})
	const [showPassword, setShowPassword] = useState(false)

	useEffect(() => {
		const allowedOrigins = [
			'https://loadfetcher.com',
			window.location.origin,
			window.location.hostname === 'localhost' ? 'http://localhost:5001' : '',
		].filter(Boolean)

		const handlePostMessage = event => {
			if (!allowedOrigins.includes(event.origin)) return

			try {
				// Check for OAuth error
				if (event.data.error) {
					setState(prevState => ({
						...prevState,
						errorText: `Authentication failed: ${event.data.message || 'Unknown error'}`,
					}))
					return
				}

				// Check for 2FA required from Google OAuth
				if (event.data.requiresTwoFactor) {
					setTwoFactorData({
						pendingLoginToken: event.data.pendingLoginToken,
						maskedEmail: event.data.maskedEmail,
					})
					return
				}

				const token = event.data.token
				const extensionToken = event.data?.extensionToken
				const params = new URLSearchParams(location.search)

				if (!token) return

				if (token) localStorage.setItem('jwtToken', token)

				if (sourceExtension && windowOpender) {
					if (!extensionToken) {
						setState(prevState => ({
							...prevState,
							errorText: 'Extension authentication failed: No extension token received',
						}))
						return
					}

					windowOpender?.postMessage(
						{
							source: 'loadfetcherLogin',
							token: extensionToken,
						},
						'*'
					)

					window.close()
				} else if (params.get('source') === 'extension' && window.opener) {
					if (!extensionToken) {
						setState(prevState => ({
							...prevState,
							errorText: 'Extension authentication failed: No extension token received',
						}))
						return
					}

					setSourceExtension(true)

					window.opener?.postMessage(
						{
							source: 'loadfetcherLogin',
							token: extensionToken,
						},
						'*'
					)

					window.close()
				} else {
					token && localStorage.setItem('jwtToken', token)
					event.data.email && localStorage.setItem('email', event.data.email)

					try {
						if (extensionToken) {
							sendTokenToChromeExtension(extensionId, {
								extensionToken,
							})
						}
					} catch (error) {
						// Ignore extension messaging errors
					}

					const user = token && jwtDecode(token)
					const trial_end = new Date(user?.trial_end)
					const period_end = new Date(user?.period_end)

					const now = new Date()
					const isExpired = period_end < now || trial_end < now

					if (token && event.data.email && !isExpired) {
						window.location = '/loadboard'
					} else if (isExpired) {
						window.location = '/plans'
					}
				}
			} catch (error) {
				setState(prevState => ({
					...prevState,
					errorText: `Login error: ${error.message}`,
				}))
			}
		}

		window.addEventListener('message', handlePostMessage)

		return () => {
			window.removeEventListener('message', handlePostMessage)
		}
	}, [location])

	const onChange = e => {
		e.persist()
		setState(prevState => ({
			...prevState,
			loading: false,
			[e.target.name]: e.target.value,
			errorText: '',
		}))
	}

	const onSubmit = async e => {
		e.preventDefault()

		setState(prevState => ({
			...prevState,
			loading: true,
		}))

		try {
			setLoading(true)
			const result = await login({
				user: state,
				location,
				navigate,
				sourceExtension,
				windowOpender,
				userToken,
			})

			if (result?.requiresTwoFactor) {
				setTwoFactorData({
					pendingLoginToken: result.pendingLoginToken,
					maskedEmail: result.maskedEmail,
				})
				setLoading(false)
				return
			}

			// Track successful login
			posthogService.track('user_logged_in', {
				method: 'password',
				source: sourceExtension ? 'extension' : 'web',
				has_2fa: false,
			})

			setLoading(false)
		} catch (err) {
			console.log('login error', err)

			let errorText = err?.response?.data?.error
				? err.response.data.error
				: 'An error occurred while logging in.'

			// Track failed login
			posthogService.track('login_failed', {
				method: 'password',
				error: errorText,
			})

			setState(prevState => ({
				...prevState,
				loading: false,
				errorText: errorText,
			}))

			setLoading(false)
		}
	}

	const handlePasskeyLogin = async () => {
		try {
			setLoading(true)
			setState(prev => ({ ...prev, errorText: '' }))

			const params = new URLSearchParams(location.search)
			const result = await loginWithPasskey(
				state.email,
				sourceExtension,
				userToken,
				extensionOnly
			)

			if (result?.requiresTwoFactor) {
				setTwoFactorData({
					pendingLoginToken: result.pendingLoginToken,
					maskedEmail: result.maskedEmail,
				})
				setLoading(false)
				return
			}

			if (result?.token || result?.extensionToken) {
				const token = result.token
				const extensionToken = result.extensionToken

				if (sourceExtension && windowOpender) {
					if (!extensionToken) {
						setState(prev => ({
							...prev,
							errorText: 'Extension authentication failed: No extension token received',
						}))
						return
					}

					windowOpender?.postMessage(
						{
							source: 'loadfetcherLogin',
							token: extensionToken,
						},
						'*'
					)

					localStorage.clear()
					window.close()
				} else if (params.get('source') === 'extension' && window.opener) {
					if (!extensionToken) {
						setState(prev => ({
							...prev,
							errorText: 'Extension authentication failed: No extension token received',
						}))
						return
					}

					setSourceExtension(true)

					window.opener?.postMessage(
						{
							source: 'loadfetcherLogin',
							token: extensionToken,
						},
						'*'
					)

					localStorage.clear()
					window.close()
				} else {
					localStorage.setItem('jwtToken', token)

					if (extensionToken) {
						try {
							sendTokenToChromeExtension(extensionId, { extensionToken })
						} catch (error) {
							// Ignore extension messaging errors
						}
					}

					const user = jwtDecode(token)
					const trial_end = new Date(user?.trial_end)
					const period_end = new Date(user?.period_end)
					const now = new Date()
					const isExpired = period_end < now || trial_end < now

					if (!isExpired) {
						window.location = '/loadboard'
					} else {
						window.location = '/plans'
					}
				}
			}
		} catch (err) {
			const isUserCancellation = err.name === 'NotAllowedError'
			if (isUserCancellation) return

			const errorMessage = err.response?.data?.error || 'Passkey authentication failed. Please try again.'
			setState(prev => ({
				...prev,
				errorText: errorMessage,
			}))
		} finally {
			setLoading(false)
		}
	}

	const handleTwoFactorSuccess = data => {
		if (!data.token && !data.extensionToken) return

		const token = data.token
		const extensionToken = data.extensionToken
		const params = new URLSearchParams(location.search)

		if (sourceExtension && windowOpender) {
			if (!extensionToken) {
				setState(prev => ({
					...prev,
					errorText: 'Extension authentication failed: No extension token received',
				}))
				setTwoFactorData(null)
				return
			}

			windowOpender?.postMessage(
				{
					source: 'loadfetcherLogin',
					token: extensionToken,
				},
				'*'
			)

			localStorage.clear()
			window.close()
		} else if (params.get('source') === 'extension' && window.opener) {
			if (!extensionToken) {
				setState(prev => ({
					...prev,
					errorText: 'Extension authentication failed: No extension token received',
				}))
				setTwoFactorData(null)
				return
			}

			setSourceExtension(true)

			window.opener?.postMessage(
				{
					source: 'loadfetcherLogin',
					token: extensionToken,
				},
				'*'
			)

			localStorage.clear()
			window.close()
		} else {
			localStorage.setItem('jwtToken', token)

			if (extensionToken) {
				try {
					sendTokenToChromeExtension(extensionId, { extensionToken })
				} catch (error) {
					// Ignore extension messaging errors
				}
			}

			const user = jwtDecode(token)
			const trial_end = new Date(user?.trial_end)
			const period_end = new Date(user?.period_end)
			const now = new Date()
			const isExpired = period_end < now || trial_end < now

			if (!isExpired) {
				window.location = '/loadboard'
			} else {
				window.location = '/plans'
			}
		}
	}

	if (emailCodeMode) {
		return (
			<EmailCodeSignIn
				sourceExtension={sourceExtension}
				extensionOnly={extensionOnly}
				userToken={userToken}
				onCancel={() => setEmailCodeMode(false)}
				onSuccess={handleTwoFactorSuccess}
			/>
		)
	}

	if (twoFactorData) {
		return (
			<TwoFactorVerification
				pendingLoginToken={twoFactorData.pendingLoginToken}
				maskedEmail={twoFactorData.maskedEmail}
				extensionOnly={extensionOnly}
				onCancel={() => setTwoFactorData(null)}
				onSuccess={handleTwoFactorSuccess}
			/>
		)
	}

	return (
		<SlimLayout>
			{!sourceExtension && (
				<div className="flex">
					<>
						<Link to="/" aria-label="Home">
							<Logo
								color={'text-gray-600 text-lg'}
								height={'45px'}
								width={'45px'}
							/>
						</Link>
					</>
				</div>
			)}

			<h2 className="mt-3 mb-4 text-lg font-semibold text-gray-900">
				Sign in to your account
			</h2>

			<button
				className="google-button w-full inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-indigo-500"
				type="button"
				onClick={() => {
					const w = 500
					const h = 600
					const y = window.top.outerHeight / 2 + window.top.screenY - h / 2
					const x = window.top.outerWidth / 2 + window.top.screenX - w / 2

					const authWindow = window.open(
						googleAuthUrl,
						'Google Sign-In',
						`toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, copyhistory=no, width=${w}, height=${h}, top=${y}, left=${x}`
					)

					if (!authWindow) {
						setState(prevState => ({
							...prevState,
							errorText: 'Failed to open Google sign-in window. Please allow popups and try again.',
						}))
					}
				}}
			>
				<img
					src={googleIcon}
					alt="Google"
					style={{ width: '20px', height: '20px' }}
				/>

				<p className="btn-text">Continue with Google</p>
			</button>

			<button
				className="google-button w-full inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-indigo-500 mt-2"
				type="button"
				onClick={handlePasskeyLogin}
				disabled={loading}
			>
				<svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
				</svg>
				<p className="btn-text">Sign in with Passkey</p>
			</button>

			<button
				className="google-button w-full inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-indigo-500 mt-2"
				type="button"
				onClick={() => setEmailCodeMode(true)}
				disabled={loading}
			>
				<svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
				</svg>
				<p className="btn-text">Sign in with a code</p>
			</button>

			<div className="divider text-gray-800">OR</div>

			<form onSubmit={onSubmit} className="mt-1 grid grid-cols-1 gap-y-8">
				{/* Error message display */}
				{state.errorText && (
					<div className="error-message text-red-500 mt-2">
						{state.errorText}
						{state.errorText === 'Invalid email or password' && (
							<p className="text-gray-500 text-xs mt-1 font-normal">
								Having trouble? Try signing in with a code, Google, or a passkey
								above.
							</p>
						)}
					</div>
				)}

				<TextField
					className="col-span-full -my-2"
					label="Email address"
					id="email"
					name="email"
					type="email"
					autoComplete="email"
					required
					onChange={onChange}
				/>

				<PasswordField
					className="col-span-full -my-4"
					label="Password"
					id="password"
					name="password"
					autoComplete="current-password"
					required
					onChange={onChange}
					showPassword={showPassword}
					onTogglePassword={() => setShowPassword(!showPassword)}
				/>

				<div>
					<button
						disabled={loading}
						type="submit"
						className="submit-button group inline-flex items-center justify-center py-2 px-4 text-sm font-semibold bg-blue-600 text-white hover:text-gray-900 hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus-visible:outline-blue-600 rounded-full w-full"
					>
						{loading && (
							<span className="loading loading-spinner loading-md mr-2"></span>
						)}

						<span>
							Sign in <span aria-hidden="true">&rarr;</span>
						</span>
					</button>
				</div>

				{/* Links for Password Reset and Signup */}
				<div className="col-span-full flex justify-between items-center">
					<p className="text-sm text-gray-700">
						Don't have an account?{' '}
						<Link
							to={`/account/signup${
								sourceExtension ? '?source=extension&token=' + userToken : ''
							}`}
							className="font-medium text-blue-600 hover:underline"
						>
							Sign up
						</Link>
					</p>

					<Link
						to={`/password-reset-request${
							sourceExtension ? '?source=extension&token=' + userToken : ''
						}`}
						className="text-sm text-blue-600 hover:underline"
					>
						Forgot password?
					</Link>
				</div>

				{/* Terms & Privacy Policy Notice */}
				<div className="col-span-full text-xs text-gray-600 text-center -mt-4">
					By continuing to use our Service, you agree to our{' '}
					<Link to="/terms" target="_blank" className="font-medium text-blue-600 hover:underline">
						Terms & Conditions
					</Link>{' '}
					and{' '}
					<Link to="/policy" target="_blank" className="font-medium text-blue-600 hover:underline">
						Privacy Policy
					</Link>
				</div>
			</form>
		</SlimLayout>
	)
}
